import React from "react";

import LogClient from "@mnemonic/logging";
import { RemoveSplash } from "@mnemonic/mui";

import { safeStringifyForLog } from "../Utils/safeStringifyForLog";

export const DEFAULT_ERROR_KEY = "__default__";

export interface IErrorBoundaryProps {
  handlers: { [key: string]: { Component: typeof React.Component | React.ElementType; onError?: () => void } };
  children: React.ReactNode;
}

interface IErrorBoundaryState {
  error: null | Error;
  info;
  Component: typeof React.Component;
}

export class ErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
  public static defaultProps = {
    history: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      Component: null,
      error: null,
      info: null,
    };
  }

  public componentDidCatch(error, info) {
    const { children, ...restProps } = this.props;

    LogClient.getInstance()?.logException(error, {
      componentStack: info.componentStack,
      props: safeStringifyForLog(restProps),
    });

    // Check if we should handle it
    let handler;
    Object.keys(this.props.handlers).forEach((k) => {
      if (handler) return;
      if (error.name === k) {
        handler = this.props.handlers[k];
      }
    });

    // If a default handler is sat use that
    if (!handler && Object.prototype.hasOwnProperty.call(this.props.handlers, DEFAULT_ERROR_KEY)) {
      handler = this.props.handlers[DEFAULT_ERROR_KEY];
    }

    // Not handle
    if (!handler) throw error;

    // Handle it
    const { Component, onError } = handler;
    if (onError) {
      onError.call(this, error, info);
    }
    this.setState({ error, info, Component });
  }

  public render() {
    const { error, info, Component } = this.state;
    const { children, ...restProps } = this.props;
    if (error && Component) {
      return (
        <div style={{ marginTop: "64px" }}>
          <RemoveSplash />
          <Component {...restProps} {...{ error, info }} />
        </div>
      );
    }
    return this.props.children;
  }
}
